import { InformationCircleIcon } from "@heroicons/react/20/solid";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";
import { Button } from "@/atoms-react/button/Button.react";
import { Dropdown } from "@/atoms-react/dropdown/index.react";
import Flag from "@/atoms-react/flag/Flag.react";
import InputLabel from "@/atoms-react/input/Input.react";
import InputDate from "@/atoms-react/input/InputDate.react";
import Loader from "@/atoms-react/loader/Loader.react";
import { RadioCard } from "@/atoms-react/radio-card/index.react";
import { Base, Label } from "@/atoms-react/text/Text.react";
import { MiniAnimatedShield } from "@/components/animations/minishield/index.react";
import { role } from "@/features/auth/types";
import { LegalEntityCreateSchema, rcsResponseType } from "@/features/company/types";
import { setEmptyOrStr } from "@/features/company/utils";
import { useUser } from "@/features/user/hooks/use-user";
import { FullCountry, StaticActivity } from "@/features/user/types";
import { FAVOURITE_COUNTRY_CODES } from "@/features/utils/constants";
import { usei18n } from "@/features/utils/hooks/use-i18n";
import { useAppSelector } from "@/redux/hooks";
import { getUserRoles } from "@/redux/slices/userSlice";

type AddCompanyFormPrps = {
  onValidate: (data?: LegalEntityCreateSchema) => Promise<void>;
  countries: FullCountry[];
  isPendingValidation: boolean;
  activitiesNace:
    | {
        activities_nace: StaticActivity[];
      }
    | undefined;
  rcsCompany: rcsResponseType | undefined;
  loadingActivitiesNace: boolean;
};
export const AddCompanyForm = ({
  countries,
  onValidate,
  isPendingValidation,
  activitiesNace,
  loadingActivitiesNace,
  rcsCompany
}: AddCompanyFormPrps) => {
  const { t } = useTranslation(["pass"]);
  const labelInputDivClassName = "md:flex mt-[24px] items-center gap-6 ";
  const labelClassName = "flex w-40 min-w-40 mb-2 md:mb-0";
  const inputClassName = "flex grow ";
  const { getLanguage } = usei18n();
  const { useLegalForm } = useUser();
  const userRoles = useAppSelector(getUserRoles);

  const isAdmin = userRoles.includes(role.ADMIN);
  const { data: legalForms, isPending: loadingLf } = useLegalForm();
  const {
    handleSubmit,
    control,
    formState: { errors },
    watch
  } = useForm<LegalEntityCreateSchema>({
    defaultValues: {
      address: {
        address_1: rcsCompany?.legalEntity.address.address_1,
        zip_code: rcsCompany?.legalEntity.address.zip_code,
        city: rcsCompany?.legalEntity.address.city,
        country_id: rcsCompany?.legalEntity.address.country_id
      },
      RCS_number: rcsCompany?.legalEntity.RCS_number ?? null,
      country_incorporation_id: rcsCompany?.legalEntity.country_incorporation_id ?? null,
      date_incorporation: rcsCompany?.legalEntity.date_incorporation
        ? new Date(rcsCompany?.legalEntity.date_incorporation.toString() as string)
        : null,
      legal_name: rcsCompany?.legalEntity.legal_name ?? null,
      address_activity: null,
      website_url: null,
      brand: null,
      tax_address: null,
      lei_number: null,
      legal_form: null,
      legal_form_rcs: rcsCompany?.legalEntity.legal_form ?? null
    }
  });

  const onSubmit: SubmitHandler<LegalEntityCreateSchema> = (data) => {
    if (data.tax_address?.country_id === null || !data.tax_address?.country_id)
      data.tax_address = null;
    if (data.address_activity?.address_1 === null) data.address_activity = null;

    onValidate(data);
  };
  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="mt-5 flex flex-col justify-between border-2 border-slate-50 p-4 dark:border-slate-700">
        <div className="flex flex-row">
          <div
            className={twMerge(
              `flex w-full flex-start justify-center rounded-sm bg-bottom bg-cover md:w-1/5 lg:px-5`,
              "h-[100px] bg-[url('/images/olkypass/declarative_kyc_bg.png')]"
            )}
          >
            <div className={"flex flex-col justify-center"}>
              <MiniAnimatedShield
                style="bottom-14 left-4 w-5 h-3"
                colors="#D11E1F"
                percentage={45}
              />
            </div>
          </div>
          <div className="mt-2 flex w-full w-full flex-row content-center justify-center md:mt-0 md:ml-4 md:flex-col">
            <div className="flex flex-row whitespace-nowrap">
              <Base className="font-semibold text-[24px] text-slate-500">
                {t("olkypass.mainPage.olkypass.levelPage.status_declarative")}
              </Base>
              <InformationCircleIcon
                className="ml-2 h-5 w-5 rounded-full bg-primary text-white dark:text-slate-900"
                data-tooltip={t("olkypass.mainPage.olkypass.levelPage.declarative_sub_description")}
                data-position="bottom"
              />
            </div>
          </div>
        </div>
        <div className='div className="flex mb-5 justify-between'>
          <Base className="mt-6 hidden text-[20px] text-slate-300 md:block">
            {t("olkypass.accountPage.basicInfo.tabs.legal_entity_information")}
          </Base>
        </div>
        <div className={twMerge(labelInputDivClassName)}>
          <Label className={labelClassName}>{t("business.input.company_rcs_label")}</Label>
          <div className={"flex grow"}>
            <Controller
              control={control}
              render={({ field: { onChange, value } }) => (
                <InputLabel
                  data-tooltip={t("business.input.company_rcs_label")}
                  placeholder={t("business.input.company_rcs_placeholder")}
                  error={errors.RCS_number ? t("atoms:general.required") : ""}
                  value={value as string}
                  onChange={onChange}
                  required={false}
                />
              )}
              name="RCS_number"
            />
          </div>
        </div>
        <div className={twMerge(labelInputDivClassName)}>
          <Label className={labelClassName}>* {t("business.input.company_name_label")}</Label>
          <div className={inputClassName}>
            <Controller
              control={control}
              rules={{
                required: true
              }}
              render={({ field: { onChange, value } }) => (
                <InputLabel
                  required={false}
                  data-tooltip={t("business.input.company_name_placeholder")}
                  placeholder={""}
                  noLatinCheck={true}
                  value={value as string}
                  error={errors.legal_name ? t("atoms:general.required") : ""}
                  onChange={onChange}
                />
              )}
              name="legal_name"
            />
          </div>
        </div>
        <div className={twMerge(labelInputDivClassName)}>
          <Label className={labelClassName}>
            * {t("business.input.country_date_registration")}
          </Label>
          <Controller
            control={control}
            rules={{
              required: true
            }}
            render={({ field: { onChange, value } }) => (
              <Dropdown
                required={!!errors.country_incorporation_id}
                touched={!!errors.country_incorporation_id}
                placeholder={t("flow.id_control.input.country_of_residence_placeholder")}
                data-tooltip={t("olkypass.companyPage.basicInfo.input.country")}
                search={true}
                items={
                  (countries ?? []).map((country) => ({
                    key: country.id.toString(),
                    value: country.defaultName,
                    icon: <Flag countryCode={country.alpha2} />,
                    render: ({ key }) => (
                      <div className={"flex w-full pr-5"} key={key}>
                        <Flag countryCode={country.alpha2} className="!w-6 !h-6 rounded-sm" />
                        <Base className={"ml-2"}>{country.defaultName}</Base>
                      </div>
                    ),
                    groupKey: FAVOURITE_COUNTRY_CODES.includes(country.alpha2.toLowerCase())
                      ? "default"
                      : undefined
                  })) ?? []
                }
                value={value}
                autoComplete="country"
                className={twMerge(
                  "mb-[24px] w-62 w-full md:mb-0",
                  errors.country_incorporation_id && "border-red-500"
                )}
                onChange={(value) => {
                  onChange(value.key);
                }}
              />
            )}
            name="country_incorporation_id"
          />
          <Controller
            control={control}
            rules={{
              required: true
            }}
            render={({ field: { onChange, value } }) => (
              <InputDate
                autoComplete="bday"
                required={false}
                value={(value as Date) ?? null}
                className={twMerge("w-full", errors.date_incorporation && "border-red-500")}
                placeholder={t("flow.id_control.input.birth_date_placeholder")}
                onChange={(value) => {
                  onChange(value);
                }}
              />
            )}
            name="date_incorporation"
          />
        </div>
        {isAdmin && !!rcsCompany?.legalEntity?.legal_form && (
          <div className={twMerge(labelInputDivClassName)}>
            <Label className={labelClassName}>
              {`* Rcs ${t("olkypass.companyPage.basicInfo.input.legalForm")}`}
            </Label>
            <InputLabel disabled={true} value={rcsCompany?.legalEntity.legal_form} />
          </div>
        )}
        <div className={twMerge(labelInputDivClassName)}>
          <Label className={labelClassName}>
            * {t("olkypass.companyPage.basicInfo.input.legalForm")}
          </Label>
          <div className={inputClassName}>
            {loadingLf ? (
              <Loader />
            ) : (
              <Controller
                control={control}
                rules={{ required: true }}
                render={({ field: { onChange, value } }) => (
                  <Dropdown
                    dataCy="dropdown-legal-forms"
                    placeholder={""}
                    search={true}
                    items={(legalForms ?? []).map((lf) => ({
                      key: lf.code,
                      value: lf.name
                    }))}
                    required={!!errors.legal_form}
                    touched={!!errors.legal_form}
                    value={value}
                    autoComplete="country"
                    className={twMerge("w-full", errors.legal_form && "border-red-500")}
                    data-tooltip={t("olkypass.companyPage.basicInfo.input.legalForm")}
                    onChange={({ key }) => {
                      onChange(key);
                    }}
                  />
                )}
                name="legal_form"
              />
            )}
          </div>
        </div>
        <div className={twMerge(labelInputDivClassName, "w-full")}>
          <Label className={labelClassName}>
            {`* `}
            {t("olkypass.companyPage.basicInfo.input.activity_sector_nace")}
          </Label>
          <div className={inputClassName}>
            <div className="flex w-full flex-col">
              {loadingActivitiesNace ? (
                <Loader />
              ) : (
                <Controller
                  control={control}
                  rules={{
                    required: true
                  }}
                  render={({ field: { onChange, value } }) => (
                    <Dropdown
                      required={!!errors.activity_code}
                      touched={!!errors.activity_code}
                      value={value}
                      placeholder={t("olkypass.companyPage.basicInfo.input.activity_sector_nace")}
                      data-tooltip={t("olkypass.companyPage.basicInfo.input.activity_sector_nace")}
                      items={(activitiesNace?.activities_nace ?? []).map((code) => ({
                        key: code.Code.toString(),
                        value: code.defaultName
                      }))}
                      search={true}
                      className={twMerge("w-full", errors.activity_code && "border-red-500")}
                      onChange={({ key }) => onChange(key)}
                    />
                  )}
                  name="activity_code"
                />
              )}
            </div>
          </div>
        </div>
        <hr className="mt-6 border-slate-50 dark:border-slate-700"></hr>
        <div className='div className="flex mb-5 justify-between'>
          <Base className="mt-6 hidden text-slate-300 md:block">
            {t("olkypass.companyPage.basicInfo.input.address")}
          </Base>
        </div>
        <div className={twMerge(labelInputDivClassName, "w-full")}>
          <Label className={labelClassName}>{t("olkypass.companyPage.basicInfo.input.road")}</Label>
          <div className={inputClassName}>
            <Controller
              control={control}
              rules={{
                required: true
              }}
              render={({ field: { onChange, value } }) => (
                <InputLabel
                  required={false}
                  data-tooltip={t("olkypass.companyPage.basicInfo.input.road")}
                  placeholder={t("flow.id_control.input.address_placeholder")}
                  error={errors.address?.address_1 ? t("atoms:general.required") : ""}
                  value={value as string}
                  onChange={(value) => {
                    onChange(value);
                  }}
                />
              )}
              name="address.address_1"
            />
          </div>
        </div>
        <div className={twMerge(labelInputDivClassName)}>
          <Label className={labelClassName}>
            {t("olkypass.companyPage.basicInfo.input.city_zip")}
          </Label>
          <div className={inputClassName + "flex flex-row justify-between gap-6"}>
            <Controller
              control={control}
              rules={{
                required: true
              }}
              render={({ field: { onChange, value } }) => (
                <InputLabel
                  required={false}
                  data-tooltip={t("flow.id_control.input.business.city_label")}
                  placeholder={t("flow.id_control.input.business.city_placeholder")}
                  error={errors.address?.city ? t("atoms:general.required") : ""}
                  value={value as string}
                  onChange={(value) => {
                    onChange(value);
                  }}
                />
              )}
              name="address.city"
            />
            <Controller
              control={control}
              rules={{
                required: true
              }}
              render={({ field: { onChange, value } }) => (
                <InputLabel
                  required={false}
                  data-tooltip={t("flow.id_control.input.zip_label")}
                  placeholder={t("flow.id_control.input.zip_placeholder")}
                  error={errors.address?.zip_code ? t("atoms:general.required") : ""}
                  value={value as string}
                  onChange={(value) => {
                    onChange(value);
                  }}
                />
              )}
              name="address.zip_code"
            />
          </div>
        </div>
        <div className={twMerge(labelInputDivClassName)}>
          <Label className={labelClassName}>
            {t("olkypass.companyPage.basicInfo.input.country")}
          </Label>
          <div className={inputClassName}>
            <Controller
              control={control}
              rules={{
                required: true
              }}
              render={({ field: { onChange, value } }) => (
                <Dropdown
                  required={!!errors.address?.country_id}
                  touched={!!errors.address?.country_id}
                  search={true}
                  placeholder={t("flow.id_control.input.country_of_residence_placeholder")}
                  data-tooltip={t("olkypass.companyPage.basicInfo.input.country")}
                  items={
                    (countries ?? []).map((country) => ({
                      key: country.id.toString(),
                      value: country.defaultName,
                      icon: <Flag countryCode={country.alpha2} />,
                      render: ({ key }) => (
                        <div className={"flex w-full"} key={key}>
                          <Flag countryCode={country.alpha2} className="!w-6 !h-6 rounded-sm" />
                          <Base className={"ml-2"}>{country.defaultName}</Base>
                        </div>
                      ),
                      groupKey: FAVOURITE_COUNTRY_CODES.includes(country.alpha2.toLowerCase())
                        ? "default"
                        : undefined
                    })) ?? []
                  }
                  value={value}
                  autoComplete="country"
                  className="w-full"
                  onChange={(value) => {
                    onChange(value.key);
                  }}
                />
              )}
              name="address.country_id"
            />
          </div>
        </div>
        <hr className="mt-6 border-slate-50 dark:border-slate-700"></hr>
        <div className='div className="flex mb-5 justify-between'>
          <Base className="mt-6 hidden text-slate-300 md:block">
            {t("olkypass.companyPage.basicInfo.input.principal_address")}
          </Base>
        </div>
        <div className={twMerge(labelInputDivClassName, "mt-2")}>
          <Label className={labelClassName}>
            {t("olkypass.companyPage.basicInfo.input.road_activity")}
          </Label>
          <div className={inputClassName}>
            <Controller
              control={control}
              render={({ field: { onChange, value } }) => (
                <InputLabel
                  required={false}
                  data-tooltip={t("olkypass.companyPage.basicInfo.input.road")}
                  placeholder={t("flow.id_control.input.address_placeholder")}
                  value={value as string}
                  onChange={(value) => {
                    onChange(setEmptyOrStr(value));
                  }}
                />
              )}
              name="address_activity.address_1"
            />
          </div>
        </div>
        <div className={twMerge(labelInputDivClassName)}>
          <Label className={labelClassName}>
            {t("olkypass.companyPage.basicInfo.input.city_zip_activity")}
          </Label>
          <div className={inputClassName + "flex flex-row justify-between gap-6"}>
            <Controller
              control={control}
              render={({ field: { onChange, value } }) => (
                <InputLabel
                  required={false}
                  data-tooltip={t("flow.id_control.input.business.city_label")}
                  placeholder={t("flow.id_control.input.business.city_placeholder")}
                  value={value as string}
                  onChange={(value) => {
                    onChange(setEmptyOrStr(value));
                  }}
                />
              )}
              name="address_activity.city"
            />
            <Controller
              control={control}
              render={({ field: { onChange, value } }) => (
                <InputLabel
                  required={false}
                  data-tooltip={t("flow.id_control.input.zip_label")}
                  placeholder={t("flow.id_control.input.zip_placeholder")}
                  value={value as string}
                  onChange={(value) => {
                    onChange(setEmptyOrStr(value));
                  }}
                />
              )}
              name="address_activity.zip_code"
            />
          </div>
        </div>
        <div className={twMerge(labelInputDivClassName)}>
          <Label className={labelClassName}>
            {t("olkypass.companyPage.basicInfo.input.country_activity")}
          </Label>
          <div className={inputClassName}>
            <Controller
              control={control}
              render={({ field: { onChange, value } }) => (
                <Dropdown
                  search={true}
                  placeholder={t("flow.id_control.input.country_of_residence_placeholder")}
                  data-tooltip={t("olkypass.companyPage.basicInfo.input.country_activity")}
                  items={
                    (countries ?? []).map((country) => ({
                      key: country.id.toString(),
                      value: country.defaultName,
                      icon: <Flag countryCode={country.alpha2} />,
                      render: ({ key }) => (
                        <div className={"flex w-full"} key={key}>
                          <Flag countryCode={country.alpha2} className="!w-6 !h-6 rounded-sm" />
                          <Base className={"ml-2"}>{country.defaultName}</Base>
                        </div>
                      ),
                      groupKey: FAVOURITE_COUNTRY_CODES.includes(country.alpha2.toLowerCase())
                        ? "default"
                        : undefined
                    })) ?? []
                  }
                  value={value as string}
                  autoComplete="country"
                  className={twMerge("w-full", errors.activity_code && "border-red-500")}
                  onChange={(value) => {
                    onChange(value.key);
                  }}
                />
              )}
              name="address_activity.country_id"
            />
          </div>
        </div>
        <hr className="mt-6 border-slate-50 dark:border-slate-700"></hr>
        <div className='div className="flex mb-5 justify-between'>
          <Base className="mt-6 hidden text-slate-300 md:block">
            {t("olkypass.companyPage.basicInfo.input.other_info_mendatory")}
          </Base>
        </div>
        <div className={twMerge(labelInputDivClassName)}>
          <Label className={labelClassName}>{t("business.label.legal_entity")} </Label>
          <Controller
            control={control}
            rules={{
              validate: (x) => x !== undefined
            }}
            name="shareholder_is_legal_entity"
            render={({ field: { onChange, value } }) => (
              <div className="flex w-full flex-col">
                <div className={inputClassName + "grid grid-cols-1 gap-6 md:grid-cols-2"}>
                  <RadioCard
                    dataCy="shareholder_is_legal_entity-yes"
                    active={value == true}
                    onClick={() => onChange(true)}
                    className={twMerge(
                      "border-2 border-slate-50 dark:border-slate-700 dark:text-white",
                      errors.shareholder_is_legal_entity && "border-red-500"
                    )}
                  >
                    {getLanguage() === "fr" ? "Oui" : "Yes"}
                  </RadioCard>
                  <RadioCard
                    className={twMerge(
                      "border-2 border-slate-50 dark:border-slate-700 dark:text-white",
                      errors.shareholder_is_legal_entity ? "border-red-500" : ""
                    )}
                    active={value == false}
                    onClick={() => onChange(false)}
                  >
                    {getLanguage() === "fr" ? "Non" : "No"}
                  </RadioCard>
                </div>
              </div>
            )}
          />
        </div>
        <hr className="mt-6 border-slate-50 dark:border-slate-700"></hr>
        <div className='div className="flex mb-5 justify-between'>
          <Base className="mt-6 hidden text-slate-300 md:block">
            {t("olkypass.companyPage.basicInfo.input.other_info")}
          </Base>
        </div>
        <div className={twMerge(labelInputDivClassName)}>
          <Label className={labelClassName}>
            {t("olkypass.companyPage.basicInfo.input.website_url")}
          </Label>
          <div className={inputClassName}>
            <Controller
              control={control}
              render={({ field: { onChange, value } }) => (
                <InputLabel
                  required={false}
                  data-tooltip={t("olkypass.companyPage.basicInfo.input.website_url")}
                  value={value as string}
                  onChange={(value) => {
                    onChange(setEmptyOrStr(value));
                  }}
                />
              )}
              name="website_url"
            />
          </div>
        </div>
        <div className={twMerge(labelInputDivClassName)}>
          <Label className={labelClassName}>
            {t("olkypass.companyPage.basicInfo.input.brand")}
          </Label>
          <div className={inputClassName}>
            <Controller
              control={control}
              render={({ field: { onChange, value } }) => (
                <InputLabel
                  required={false}
                  data-tooltip={t("olkypass.companyPage.basicInfo.input.brand")}
                  value={value as string}
                  onChange={(value) => {
                    onChange(setEmptyOrStr(value));
                  }}
                />
              )}
              name="brand"
            />
          </div>
        </div>
        <div className={twMerge(labelInputDivClassName)}>
          <Label className={labelClassName}>
            {t("olkypass.companyPage.basicInfo.input.vatNumber")}
          </Label>
          <div className={inputClassName}>
            <Controller
              control={control}
              render={({ field: { onChange, value } }) => (
                <InputLabel
                  required={false}
                  data-tooltip={t("olkypass.companyPage.basicInfo.input.vatNumber")}
                  value={value as string}
                  onChange={(value) => {
                    onChange(setEmptyOrStr(value));
                  }}
                />
              )}
              name="VAT_Number"
            />
          </div>
        </div>
        <div className={twMerge(labelInputDivClassName)}>
          <Label className={labelClassName}>{t("flow.id_control.input.country_nif_label")} </Label>
          <div className={inputClassName}>
            <Controller
              control={control}
              name="tax_address.country_id"
              render={({ field: { onChange, value } }) => (
                <Dropdown
                  placeholder={t("flow.id_control.input.country_nif_label")}
                  data-tooltip={t("flow.id_control.input.country_nif_label")}
                  required={false}
                  items={
                    (countries ?? []).map((country) => ({
                      key: country.id.toString(),
                      value: country.defaultName,
                      icon: <Flag countryCode={country.alpha2} />,
                      render: ({ key }) => (
                        <div className={"flex w-full"} key={key}>
                          <Flag countryCode={country.alpha2} className="!w-6 !h-6 rounded-sm" />
                          <Base className={"ml-2"}>{country.defaultName}</Base>
                        </div>
                      ),
                      groupKey: FAVOURITE_COUNTRY_CODES.includes(country.alpha2.toLowerCase())
                        ? "default"
                        : undefined
                    })) ?? []
                  }
                  value={value}
                  autoComplete="country"
                  search={true}
                  className={twMerge("w-full", errors?.tax_address?.country_id && "border-red-500")}
                  onChange={(value) => {
                    onChange(value.key);
                  }}
                />
              )}
            />
          </div>
        </div>
        <div className={twMerge(labelInputDivClassName)}>
          <Label className={labelClassName}>{t("olkypass.companyPage.basicInfo.input.nif")}</Label>
          <div className={inputClassName}>
            <Controller
              control={control}
              disabled={watch("tax_address.country_id") === null}
              name="tax_address.tin"
              render={({ field: { onChange, value } }) => (
                <InputLabel
                  required={false}
                  data-tooltip={t("olkypass.companyPage.basicInfo.input.nif")}
                  placeholder={t("olkypass.companyPage.basicInfo.input.nif")}
                  value={value as string}
                  onChange={(value) => {
                    onChange(setEmptyOrStr(value));
                  }}
                />
              )}
            />
          </div>
        </div>
        <div className={twMerge(labelInputDivClassName)}>
          <Label className={labelClassName}>N° LEI </Label>
          <div className={inputClassName}>
            <Controller
              control={control}
              render={({ field: { onChange, value } }) => (
                <InputLabel
                  required={false}
                  value={value as string}
                  data-tooltip={"N° LEI"}
                  onChange={(value) => {
                    onChange(setEmptyOrStr(value));
                  }}
                />
              )}
              name="lei_number"
            />
          </div>
        </div>
        <div className="mt-8 flex w-full flex-row justify-center">
          <Button
            size="md"
            loading={isPendingValidation}
            type="submit"
            className="w-1/5 rounded-sm bg-primary"
          >
            {t("business.label.next")}
          </Button>
        </div>
      </div>
    </form>
  );
};
